import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Car,
  ClipboardList,
  ExternalLink,
  ListChecks,
  MailPlus,
  Pencil,
  Trash2,
  User,
} from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Dialog,
  LicensePlate,
  Skeleton,
} from "@rumbledirect/ui-v2";
import type { ConfiguratorInvite } from "@/configurator/invites/configuratorInvite";
import {
  inviteEmployeeLabel,
  isInviteExpired,
} from "@/configurator/invites/configuratorInviteFields";
import { toConfiguratorInvitePatchInput } from "@/configurator/invites/configuratorInviteForm";
import { configuratorInviteMyUrl } from "@/configurator/invites/configuratorInviteMyUrl";
import { ConfiguratorInviteSheet } from "@/configurator/invites/ConfiguratorInviteSheet";
import { useDeleteConfiguratorInvite } from "@/configurator/invites/useDeleteConfiguratorInvite";
import { useUpdateConfiguratorInvite } from "@/configurator/invites/useUpdateConfiguratorInvite";
import { useVehicleConversionOptions } from "@/configurator/invites/useVehicleConversionOptions";
import { useVehicleCategoryOptions } from "@/configurator/vehicleTypes/useVehicleCategoryOptions";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { DetailSection, FieldGrid } from "@/dossiers/DetailSection";
import { useGetAdminConfiguratorInvitesConfiguratorInviteId } from "@/lib/api/generated/configurator/configurator";
import { useGetAdminEmployeesEmployeeId } from "@/lib/api/generated/employee/employee";
import { useGetAdminVehiclesVehicleId } from "@/lib/api/generated/vehicle/vehicle";
import { formatDate, formatNumber } from "@rumbledirect/ui-v2/format";
import {
  getVehicleLicensePlate,
  getVehicleTitle,
} from "@/vehicles/vehicleFields";

const UPDATE_PERMISSION = "admin_configurator_invite_update";
const DELETE_PERMISSION = "admin_configurator_invite_delete";
const REQUEST_READ_PERMISSION = "admin_configurator_request_read_details";
const EMPLOYEE_READ_PERMISSION = "admin_employee_read_details";
const VEHICLE_READ_PERMISSION = "admin_vehicle_read_details";

export const Route = createFileRoute(
  "/_authenticated/configurator/invites_/$configuratorInviteId",
)({
  component: ConfiguratorInviteDetailPage,
});

function ConfiguratorInviteDetailPage(): ReactNode {
  const { configuratorInviteId } = Route.useParams();
  const navigate = useNavigate();

  const { data, isPending, isError, refetch } =
    useGetAdminConfiguratorInvitesConfiguratorInviteId(configuratorInviteId);

  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const updateMutation = useUpdateConfiguratorInvite(configuratorInviteId);
  const deleteMutation = useDeleteConfiguratorInvite();

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/invites"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar uitnodigingen
      </Link>

      {isError ? (
        <Card
          role="alert"
          className="flex flex-col items-center gap-3 py-10 text-center"
        >
          <p className="text-muted-foreground">
            We konden deze uitnodiging niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <>
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-64 w-full" />
        </>
      ) : (
        <>
          <InviteHeader
            invite={data}
            onEdit={() => setIsEditing(true)}
            onDelete={() => setConfirmDelete(true)}
          />

          <div className="grid gap-5 lg:grid-cols-2">
            <InviteSection invite={data} />
            <EmployeeSection invite={data} />
            {data.replacement ? <OldVehicleSection invite={data} /> : null}
            <ChoiceSection invite={data} />
          </div>

          {isEditing && (
            <ConfiguratorInviteSheet
              open
              onOpenChange={setIsEditing}
              invite={data}
              isSubmitting={updateMutation.isPending}
              onSubmit={(values) =>
                updateMutation.mutate(toConfiguratorInvitePatchInput(values), {
                  onSuccess: () => setIsEditing(false),
                })
              }
            />
          )}

          <Dialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title="Uitnodiging intrekken"
            description={`Weet je zeker dat je de uitnodiging voor ${inviteEmployeeLabel(data)} wilt intrekken?`}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteMutation.isPending}
                >
                  Annuleren
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    deleteMutation.mutate(configuratorInviteId, {
                      onSuccess: () => {
                        setConfirmDelete(false);
                        void navigate({
                          to: "/configurator/invites",
                          search: { page: 1 },
                        });
                      },
                    })
                  }
                >
                  Intrekken
                </Button>
              </>
            }
          />
        </>
      )}
    </div>
  );
}

function InviteHeader({
  invite,
  onEdit,
  onDelete,
}: {
  readonly invite: ConfiguratorInvite;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}): ReactNode {
  const expired = isInviteExpired(invite);
  // The request stands on its invite for good, so an invite that was
  // configured can no longer be withdrawn — the backend refuses it. What is
  // offered instead is the way to the request itself.
  const requestId = invite.request ?? undefined;
  // Only while the driver can still hand something in does the link into the
  // my app lead anywhere: the configurator would refuse the result otherwise.
  const myUrl = invite.openForEmployee
    ? configuratorInviteMyUrl(invite.id)
    : undefined;

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">
          {inviteEmployeeLabel(invite)}
        </h1>
        {requestId ? (
          <Badge variant="info" size="sm">
            Geconfigureerd
          </Badge>
        ) : expired ? (
          <Badge variant="neutral" size="sm">
            Verlopen
          </Badge>
        ) : (
          <Badge variant="success" size="sm">
            Actief
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        {requestId ? (
          <RequirePermission permission={REQUEST_READ_PERMISSION}>
            <Link
              to="/configurator/requests/$configuratorRequestId"
              params={{ configuratorRequestId: requestId }}
              search={{ tab: "algemeen" }}
              className={buttonVariants({ variant: "outline", size: "sm" })}
            >
              <ClipboardList className="size-4" aria-hidden="true" />
              Naar de aanvraag
            </Link>
          </RequirePermission>
        ) : null}
        {myUrl ? (
          // Opens the driver's own configurator. An external link rather than a
          // router link: the my app is a separate frontend.
          <a
            href={myUrl}
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            <ExternalLink className="size-4" aria-hidden="true" />
            Openen in my
          </a>
        ) : null}
        <RequirePermission permission={UPDATE_PERMISSION}>
          <Button size="sm" onClick={onEdit}>
            <Pencil className="size-4" aria-hidden="true" />
            Bewerken
          </Button>
        </RequirePermission>
        {requestId ? null : (
          <RequirePermission permission={DELETE_PERMISSION}>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="size-4" aria-hidden="true" />
              Intrekken
            </Button>
          </RequirePermission>
        )}
      </div>
    </div>
  );
}

/** The terms of the invite itself. */
function InviteSection({
  invite,
}: {
  readonly invite: ConfiguratorInvite;
}): ReactNode {
  return (
    <DetailSection title="Uitnodiging" icon={MailPlus}>
      <FieldGrid
        fields={[
          { label: "Uitgenodigd op", value: dateField(invite.createdAt) },
          { label: "Geldig tot", value: dateField(invite.expiresAt) },
          {
            label: "Gewenste leverdatum",
            value: dateField(invite.desiredDeliveryAt),
          },
          {
            label: "Verwachte kilometers per jaar",
            value: kilometersField(invite.expectedKilometers),
          },
          {
            label: "Looptijd contract",
            value: invite.contractDurationMonths
              ? `${invite.contractDurationMonths} maanden`
              : undefined,
          },
          {
            label: "Vervanging",
            value: invite.replacement ? "Ja" : "Nee",
          },
        ]}
      />
    </DetailSection>
  );
}

/**
 * Who the invite is for. The invite carries the driver as a bare UUID, so the
 * driver is resolved separately for anything beyond the name the output
 * already includes — and only when the user may read driver details, so the
 * page never fires a request that is bound to come back 403.
 */
function EmployeeSection({
  invite,
}: {
  readonly invite: ConfiguratorInvite;
}): ReactNode {
  const user = useCurrentUser();
  const maySeeEmployee = hasPermission(user, EMPLOYEE_READ_PERMISSION);

  const { data: employee } = useGetAdminEmployeesEmployeeId(invite.employeeId, {
    query: { enabled: maySeeEmployee && Boolean(invite.employeeId) },
  });

  return (
    <DetailSection title="Medewerker" icon={User}>
      <FieldGrid
        fields={[
          {
            label: "Naam",
            value: employee?.person?.fullName ?? invite.employeeName,
          },
          { label: "E-mailadres", value: employee?.person?.email },
          { label: "Medewerkernummer", value: employee?.employeeNumber },
        ]}
      />
    </DetailSection>
  );
}

/**
 * The vehicle this invite replaces. Resolved separately for the same reason as
 * the driver: the invite only carries its UUID and license plate.
 */
function OldVehicleSection({
  invite,
}: {
  readonly invite: ConfiguratorInvite;
}): ReactNode {
  const user = useCurrentUser();
  const maySeeVehicle = hasPermission(user, VEHICLE_READ_PERMISSION);

  const { data: vehicle } = useGetAdminVehiclesVehicleId(
    invite.oldVehicle ?? "",
    { query: { enabled: maySeeVehicle && Boolean(invite.oldVehicle) } },
  );

  const plate = vehicle
    ? getVehicleLicensePlate(vehicle)
    : invite.oldVehicleLicensePlate;

  return (
    <DetailSection
      title="Te vervangen voertuig"
      icon={Car}
      action={
        vehicle && invite.oldVehicle ? (
          <Link
            to="/vehicles/$vehicleId"
            params={{ vehicleId: invite.oldVehicle }}
            search={{ tab: "gegevens" }}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Naar voertuig
          </Link>
        ) : undefined
      }
    >
      <FieldGrid
        fields={[
          {
            label: "Kenteken",
            value: plate ? <LicensePlate value={plate} size="sm" /> : undefined,
          },
          { label: "Voertuig", value: vehicle && getVehicleTitle(vehicle) },
        ]}
      />
    </DetailSection>
  );
}

/**
 * What the driver may pick from. Both relations are stored as UUIDs, so the
 * titles are resolved through the same option hooks the invite form uses. An
 * empty selection means "no restriction" on the backend, which is spelled out
 * rather than shown as an empty list.
 */
function ChoiceSection({
  invite,
}: {
  readonly invite: ConfiguratorInvite;
}): ReactNode {
  const { options: categoryOptions } = useVehicleCategoryOptions();
  // Every conversion, not just the ones the selected categories allow: this is
  // a lookup, and a conversion whose category was changed afterwards should
  // still resolve to its title.
  const { options: conversionOptions } = useVehicleConversionOptions([]);

  const categories = useLabels(invite.vehicleCategories, categoryOptions);
  const conversions = useLabels(invite.vehicleConversions, conversionOptions);

  return (
    <DetailSection title="Keuzeruimte" icon={ListChecks}>
      {categories.length === 0 && conversions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Vrije keuze: de medewerker kan uit het volledige aanbod kiezen.
        </p>
      ) : (
        <div className="space-y-4 text-sm">
          <LabelledBadges
            title="Voertuigcategorieën"
            labels={categories}
            emptyText="Alle categorieën"
          />
          <LabelledBadges
            title="Conversies"
            labels={conversions}
            emptyText="Alle conversies binnen deze categorieën"
          />
        </div>
      )}
    </DetailSection>
  );
}

function LabelledBadges({
  title,
  labels,
  emptyText,
}: {
  readonly title: string;
  readonly labels: readonly string[];
  readonly emptyText: string;
}): ReactNode {
  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground">{title}</p>
      {labels.length === 0 ? (
        <p className="font-medium">{emptyText}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {labels.map((label) => (
            <Badge key={label} variant="neutral" size="sm">
              {label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Resolves ids to their option labels, falling back to the id itself so a
 * selection is never silently dropped while the options are still loading.
 */
function useLabels(
  ids: readonly string[] | null | undefined,
  options: readonly { readonly value: string; readonly label: string }[],
): string[] {
  return useMemo(() => {
    const labelById = new Map(
      options.map((option) => [option.value, option.label]),
    );
    return (ids ?? []).map((id) => labelById.get(id) ?? id);
  }, [ids, options]);
}

function dateField(value: string | null | undefined): string | undefined {
  return value ? (formatDate(value) ?? undefined) : undefined;
}

function kilometersField(value: number | null | undefined): string | undefined {
  return typeof value === "number" ? `${formatNumber(value)} km` : undefined;
}
